import { useState } from "react";
import { Upload, X, Check, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";

const SAMPLE = `name,email,phone,belt,stripes,status,months,notes
Mateo Álvarez,mateo@ludus.com,+507-6000-0001,blue,2,active,18,Left shoulder — go easy on kimuras
Carla Mendes,carla@ludus.com,+507-6000-0002,brown,1,active,22,
Ricardo Mora,ricardo@ludus.com,+507-6000-0003,purple,3,active,16,Competes IBJJF
Gabriel Souza,gabriel@ludus.com,,blue,0,trial,4,First BJJ experience`;

function parseCSV(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter(l => l.trim());
  if (lines.length < 2) return [];
  // Proper CSV cell splitter that respects double-quoted values (needed for notes with commas)
  const splitLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; } // escaped quote
        else inQ = !inQ;
      } else if (ch === "," && !inQ) {
        out.push(cur); cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map(s => s.trim());
  };
  const headers = splitLine(lines[0]).map(h => h.toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitLine(lines[i]);
    const row = {};
    headers.forEach((h, j) => { row[h] = cells[j] ?? ""; });
    if (row.stripes !== undefined && row.stripes !== "") row.stripes = Number(row.stripes) || 0;
    if (row.months !== undefined && row.months !== "") row.months = Number(row.months) || 0;
    if (row.name) rows.push(row);
  }
  return rows;
}

export default function ImportStudentsModal({ onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onFile = async (e) => {
    setError("");
    const f = e.target.files?.[0]; if (!f) return;
    const text = await f.text();
    const parsed = parseCSV(text);
    if (parsed.length === 0) { setError("No rows detected. Check the header row."); return; }
    setRows(parsed);
  };

  const useSample = () => { setRows(parseCSV(SAMPLE)); setError(""); };

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/students/import", { rows });
      setResult(data);
    } catch (e) { setError(formatApiError(e, "Import failed")); }
    setBusy(false);
  };

  return (
    <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"grid", placeItems:"center", zIndex:60}} onClick={onClose}>
      <div className="card p-7 w-[640px] max-w-[92vw]" onClick={e=>e.stopPropagation()} data-testid="import-modal">
        <div className="flex items-center justify-between mb-5">
          <div><div className="label" style={{color:"var(--green)"}}>Roster import</div><div className="text-[20px] font-bold uppercase mt-1 tracking-tight">Import students from CSV</div></div>
          <button onClick={onClose} data-testid="import-close"><X size={18}/></button>
        </div>

        {!result ? (
          <>
            <div className="label-tiny mb-3 leading-relaxed">
              CSV columns (case-insensitive): <span className="mono" style={{color:"var(--green)"}}>name, email, phone, belt, stripes, status, months, notes</span>. Only <span className="mono">name</span> is required. <button onClick={useSample} className="underline" style={{color:"var(--text-dim)"}} data-testid="use-sample">Use sample</button>
            </div>
            <label className="card card-hover p-6 flex flex-col items-center gap-2 cursor-pointer" data-testid="file-drop">
              <Upload size={22} style={{color:"var(--green)"}}/>
              <div className="font-semibold text-[14px]">Choose CSV file</div>
              <div className="label-tiny">UTF-8 · header row required</div>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file"/>
            </label>
            {rows.length > 0 && (
              <div className="mt-5">
                <div className="label">Preview · {rows.length} rows</div>
                <div className="card mt-2 overflow-auto" style={{maxHeight: 240}}>
                  <table className="w-full text-sm">
                    <thead><tr style={{borderBottom:"1px solid var(--border)"}}>
                      <th className="label-tiny text-left p-3">Name</th>
                      <th className="label-tiny text-left p-3">Email</th>
                      <th className="label-tiny text-left p-3">Phone</th>
                      <th className="label-tiny text-left p-3">Belt</th>
                      <th className="label-tiny text-right p-3">Stripes</th>
                      <th className="label-tiny text-left p-3">Status</th>
                    </tr></thead>
                    <tbody>
                      {rows.slice(0, 10).map((r, i) => (
                        <tr key={`${r.email || r.name}-${i}`} style={{borderBottom:"1px solid var(--border)"}}>
                          <td className="p-3">{r.name}</td>
                          <td className="p-3 label-tiny">{r.email}</td>
                          <td className="p-3 label-tiny mono">{r.phone}</td>
                          <td className="p-3 capitalize">{r.belt}</td>
                          <td className="p-3 text-right mono">{r.stripes}</td>
                          <td className="p-3"><span className={`pill pill-${r.status || "active"}`}>{r.status || "active"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rows.length > 10 && <div className="label-tiny p-3 text-center">…and {rows.length - 10} more</div>}
                </div>
              </div>
            )}
            {error && <div className="label-tiny mt-3 flex items-center gap-2" style={{color:"var(--danger)"}} data-testid="import-error"><AlertCircle size={14}/>{error}</div>}
            <button onClick={submit} disabled={busy || rows.length===0} className="btn btn-primary w-full mt-5" data-testid="import-submit">
              {busy ? "Importing…" : `Import ${rows.length || 0} students`}
            </button>
          </>
        ) : (
          <div className="text-center py-4" data-testid="import-result">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{background:"var(--green-bg)", border:"1px solid var(--green-border)"}}>
              <Check size={22} style={{color:"var(--green)"}}/>
            </div>
            <div className="text-[20px] font-bold uppercase tracking-tight">Import complete</div>
            <div className="grid grid-cols-3 gap-3 mt-5">
              <div className="card p-4"><div className="label-tiny">Added</div><div className="text-[24px] font-bold mt-1" style={{color:"var(--green)"}}>{result.added}</div></div>
              <div className="card p-4"><div className="label-tiny">Updated</div><div className="text-[24px] font-bold mt-1">{result.updated}</div></div>
              <div className="card p-4"><div className="label-tiny">Skipped</div><div className="text-[24px] font-bold mt-1" style={{color:"var(--text-dim)"}}>{result.skipped}</div></div>
            </div>
            {(result.errors || []).length > 0 && <div className="mt-4 label-tiny text-left" style={{color:"var(--warn)"}}>{result.errors.length} warnings — first few: {result.errors.slice(0,3).join("; ")}</div>}
            <button onClick={() => { onDone?.(); onClose(); }} className="btn btn-primary w-full mt-6" data-testid="import-done">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
